import NextLink from 'next/link';

import AccountCircleOutlined from '@mui/icons-material/AccountCircleOutlined';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import PreviewOutlined from '@mui/icons-material/PreviewOutlined';

import { GenericMenuItem, type GenericMenuItemProps } from '@craft/ui/components/lists';
import { type Url } from '@craft/ui/util';

import { Pages } from '@/src/config/pages';
import { Auth0Config } from '@/src/integrations/auth0';

import * as types from './types';

/**
 * Returns the appropriate 'href' for the {@link GenericMenuItem} that performs a logout process
 * when clicked.
 *
 * The type coercion to `Url` is necessary because the `GenericMenuItem` expects 'href' to be typed
 * as a "URL" template literal if we are using an HTML anchor element vs. NextJS {@link Link},
 * which means it wants a string that starts with the https/http protocol.
 *
 * 99% of the time in the application, this is correct - but not here, as the logout path is a
 * relative path but needs to be rendered as an anchor element to avoid NextJS prefetching and
 * client-side routing.
 */
const getLogoutHref = () => Auth0Config.fromEnvironment().logoutPath as Url;

type UserProfileMenuItemAnchorProps<T extends types.UserProfileMenuItemType> = {
  readonly title: string;
  readonly type: T;
} & Required<Pick<GenericMenuItemProps<'a'>, 'aria-label' | 'component' | 'href' | 'icon' | 'id'>>;

type UserProfileMenuItemLinkProps<T extends types.UserProfileMenuItemType> = {
  readonly title: string;
  readonly type: T;
} & Required<
  Pick<GenericMenuItemProps<typeof NextLink>, 'aria-label' | 'component' | 'href' | 'icon' | 'id'>
>;

type UserProfileMenuItemStaticProps<T extends types.UserProfileMenuItemType> =
  | UserProfileMenuItemAnchorProps<T>
  | UserProfileMenuItemLinkProps<T>;

const UserProfileMenuItemStaticProps: {
  [key in types.UserProfileMenuItemType]: UserProfileMenuItemStaticProps<key>;
} = {
  [types.UserProfileMenuItemType.SignOut]: {
    'aria-label': 'sign out',
    component: 'a',
    href: getLogoutHref(),
    icon: LogoutOutlined,
    id: 'sign-out-btn',
    title: 'Sign Out',
    type: types.UserProfileMenuItemType.SignOut,
  },
  [types.UserProfileMenuItemType.SwitchView]: {
    'aria-label': 'switch view',
    component: NextLink,
    href: Pages.constructPath('switch-view', {}),
    icon: PreviewOutlined,
    id: 'switch-view-btn',
    title: 'Switch View',
    type: types.UserProfileMenuItemType.SwitchView,
  },
  [types.UserProfileMenuItemType.ViewProfile]: {
    'aria-label': 'view profile',
    component: NextLink,
    href: Pages.constructPath('my-profile', {}),
    icon: AccountCircleOutlined,
    id: 'view-profile-btn',
    title: 'View Profile',
    type: types.UserProfileMenuItemType.ViewProfile,
  },
};

type UserProfileMenuItemProps<T extends types.UserProfileMenuItemType> = {
  readonly type: T;
} & Omit<
  GenericMenuItemProps<UserProfileMenuItemStaticProps<T>['component']>,
  'icon' | 'iconProps' | 'title' | 'titleProps'
>;

export const UserProfileMenuItem = <T extends types.UserProfileMenuItemType>({
  type,
  ...props
}: UserProfileMenuItemProps<T>) => (
  <GenericMenuItem<UserProfileMenuItemStaticProps<T>['component']>
    {...props}
    {...UserProfileMenuItemStaticProps[type]}
    iconProps={{ color: 'secondary.main' }}
    titleProps={{ sx: t => ({ color: t.palette.secondary.main }) }}
  />
);
